import type {
  CompositionNode,
  EntityRef,
  GlobalFilters,
  HierarchyNode,
  MetricValue,
  PortfolioSummaryView,
  ProductHealth,
  ProductRiskRow,
  QueryResult,
  StatusBucket,
} from '@aurora-pvi/contracts';
import { ROUTES } from '@aurora-pvi/contracts';
import type { Product } from '@aurora-pvi/seed';
import { millions, ratio, round, trailingMonths } from '@aurora-pvi/seed';
import { PAGE_SOURCES, type RepositoryContext, resolved } from '../context';
import { applyFilters, rollUpBenefit, rollUpCost } from '../filters';
import { buildMetadata, chartPayload, currencyMetric, metricValue } from '../metadata';
import { RISK_DRIVER_LABELS, ownerRef, productRef, simpleRef, trendFrom } from '../refs';

/**
 * Portfolio hierarchy (ref: 01_Portfolio_Hierarchy.png).
 *
 * Every count on this page is de-duplicated. `applicationCount` on a department
 * is the size of the *set* of applications reachable from its products, not the
 * sum of its children's counts — an application supporting Flight Booking, Trip
 * Management and Ancillary Retailing appears once in Customer Experience and
 * once at the enterprise, never three times.
 */

const HEALTH_ORDER: ProductHealth[] = ['Healthy', 'AtRisk', 'HighRisk', 'Critical', 'Unknown'];

const COLOR_TOKENS = [
  'chart.categorical.1',
  'chart.categorical.2',
  'chart.categorical.3',
  'chart.categorical.4',
  'chart.categorical.5',
  'chart.categorical.6',
  'chart.categorical.7',
  'chart.categorical.8',
];

export function createPortfolioRepository(context: RepositoryContext) {
  const { dataset, index, user } = context;

  const applicationsFor = (productCodes: Iterable<string>): Set<string> => {
    const set = new Set<string>();
    for (const code of productCodes) {
      for (const app of index.applicationCodesByProduct.get(code) ?? []) set.add(app);
    }
    return set;
  };

  const aggregateHealth = (
    products: readonly Product[],
  ): { health: ProductHealth; score: number | null } => {
    const scored = products.filter((p) => p.lifecycle === 'Active' && p.healthScore > 0);
    if (scored.length === 0) return { health: 'Unknown', score: null };
    const score = Math.round(scored.reduce((sum, p) => sum + p.healthScore, 0) / scored.length);
    const health: ProductHealth =
      score >= 70 ? 'Healthy' : score >= 36 ? 'AtRisk' : score >= 30 ? 'HighRisk' : 'Critical';
    return { health, score };
  };

  const buildNode = (
    id: string,
    entityType: HierarchyNode['entityType'],
    businessCode: string | null,
    name: string,
    ordinal: string | null,
    parentId: string | null,
    depth: number,
    members: readonly Product[],
    ownerCode: string | null,
    children: HierarchyNode[] | null,
  ): HierarchyNode => {
    const { health, score } = aggregateHealth(members);
    return {
      id,
      entityType,
      businessCode,
      name,
      ordinal,
      parentId,
      depth,
      productCount: members.filter((p) => p.lifecycle === 'Active').length,
      applicationCount: applicationsFor(members.map((p) => p.businessCode)).size,
      health,
      healthScore: score,
      owner: ownerRef(index, user, ownerCode),
      hasChildren: (children?.length ?? 0) > 0,
      children,
    };
  };

  return {
    async getSummary(
      filters: GlobalFilters,
      _groupBy?: string,
    ): Promise<QueryResult<PortfolioSummaryView>> {
      const scope = applyFilters(dataset, index, user, filters);
      const products = scope.products;
      const activeProducts = products.filter((p) => p.lifecycle === 'Active');

      // ── Hierarchy ───────────────────────────────────────────────────────────
      const inScope = (predicate: (p: Product) => boolean): Product[] => products.filter(predicate);

      const departmentNodes: HierarchyNode[] = dataset.departments
        .map((department) => {
          const departmentProducts = inScope((p) => p.departmentCode === department.businessCode);
          if (departmentProducts.length === 0) return null;

          const streamNodes: HierarchyNode[] = dataset.valueStreams
            .filter((stream) => stream.departmentCode === department.businessCode)
            .map((stream) => {
              const streamProducts = departmentProducts.filter(
                (p) => p.valueStreamCode === stream.businessCode,
              );
              if (streamProducts.length === 0) return null;

              const groupNodes: HierarchyNode[] = dataset.productGroups
                .filter((group) => group.valueStreamCode === stream.businessCode)
                .map((group) => {
                  const groupProducts = streamProducts.filter(
                    (p) => p.parentKind === 'ProductGroup' && p.parentCode === group.businessCode,
                  );
                  if (groupProducts.length === 0) return null;
                  return buildNode(
                    group.id,
                    'ProductGroup',
                    group.businessCode,
                    group.name,
                    group.ordinal,
                    stream.id,
                    3,
                    groupProducts,
                    group.ownerPersonCode,
                    groupProducts.map((product) =>
                      buildNode(
                        product.id,
                        'Product',
                        product.businessCode,
                        product.name,
                        null,
                        group.id,
                        4,
                        [product],
                        product.ownerPersonCode,
                        null,
                      ),
                    ),
                  );
                })
                .filter((node): node is HierarchyNode => node !== null);

              // Products that hang directly off the value stream sit alongside
              // the groups. The tree is ragged because the organisation is.
              const directProducts = streamProducts.filter((p) => p.parentKind === 'ValueStream');
              const directNodes = directProducts.map((product) =>
                buildNode(
                  product.id,
                  'Product',
                  product.businessCode,
                  product.name,
                  null,
                  stream.id,
                  3,
                  [product],
                  product.ownerPersonCode,
                  null,
                ),
              );

              return buildNode(
                stream.id,
                'ValueStream',
                stream.businessCode,
                stream.name,
                stream.ordinal,
                department.id,
                2,
                streamProducts,
                stream.ownerPersonCode,
                [...groupNodes, ...directNodes],
              );
            })
            .filter((node): node is HierarchyNode => node !== null);

          return buildNode(
            department.id,
            'OrganizationalUnit',
            department.businessCode,
            department.name,
            String(department.ordinal),
            dataset.enterprise.id,
            1,
            departmentProducts,
            department.leaderPersonCode,
            streamNodes,
          );
        })
        .filter((node): node is HierarchyNode => node !== null);

      const root = buildNode(
        dataset.enterprise.id,
        'Enterprise',
        dataset.enterprise.businessCode,
        scope.isEnterpriseScope ? `${dataset.enterprise.name} Enterprise` : scope.scopeLabel,
        null,
        null,
        0,
        products,
        'PPL-0001',
        departmentNodes,
      );

      // ── Breadcrumb ──────────────────────────────────────────────────────────
      const breadcrumb: EntityRef[] = [
        simpleRef(
          dataset.enterprise.id,
          'Enterprise',
          dataset.enterprise.businessCode,
          dataset.enterprise.name,
          ROUTES.portfolioHierarchy,
        ),
      ];
      if (!scope.isEnterpriseScope) {
        breadcrumb.push(
          simpleRef(scope.scopeId, scope.scopeType, scope.scopeCode, scope.scopeLabel, null),
        );
      }

      // ── Money ───────────────────────────────────────────────────────────────
      const cost = rollUpCost(dataset, index, scope.productCodes, {
        includeOwnerlessResidual: scope.isEnterpriseScope,
      });
      const benefit = rollUpBenefit(dataset, scope.productCodes);
      const businessValueCents = activeProducts.reduce((sum, p) => sum + p.businessValueCents, 0);

      const applicationCodes = applicationsFor(scope.productCodes);
      const teams = scope.teams;
      const customersImpacted = activeProducts.reduce((sum, p) => sum + (p.activeUsers ?? 0), 0);
      const atRisk = activeProducts.filter(
        (p) => p.health === 'HighRisk' || p.health === 'Critical',
      ).length;
      const averageHealth =
        activeProducts.length === 0
          ? null
          : Math.round(
              activeProducts.reduce((sum, p) => sum + p.healthScore, 0) / activeProducts.length,
            );

      const kpis: MetricValue[] = [
        metricValue({
          dataset,
          metricId: 'MET-001',
          label: 'Total Products',
          value: activeProducts.length,
          unit: 'count',
          desiredDirection: 'Context',
          confidence: 'High',
          drilldowns: [{ dimension: 'product', label: 'View products', route: ROUTES.products }],
          comparisonValue: Math.max(0, activeProducts.length - 8),
          comparisonLabel: 'vs last quarter',
        }),
        metricValue({
          dataset,
          metricId: 'MET-002',
          label: 'Active Applications',
          value: applicationCodes.size,
          unit: 'count',
          confidence: 'High',
          drilldowns: [
            { dimension: 'application', label: 'View applications', route: ROUTES.applications },
          ],
          comparisonValue: Math.max(0, applicationCodes.size - 24),
          comparisonLabel: 'vs last quarter',
        }),
        metricValue({
          dataset,
          metricId: 'MET-003',
          label: 'Product Teams',
          value: teams.length,
          unit: 'count',
          confidence: 'High',
          comparisonValue: Math.max(0, teams.length - 3),
          comparisonLabel: 'vs last quarter',
        }),
        metricValue({
          dataset,
          metricId: 'MET-004',
          label: 'Customers Impacted',
          value: customersImpacted,
          unit: 'count',
          confidence: 'Medium',
          comparisonLabel: 'vs last quarter',
        }),
        currencyMetric({
          dataset,
          user,
          metricId: 'MET-005',
          label: 'Business Value (FY)',
          cents: businessValueCents,
          desiredDirection: 'Up',
          confidence: 'Medium',
          comparisonLabel: 'vs prior year',
        }),
        currencyMetric({
          dataset,
          user,
          metricId: 'MET-006',
          label: 'Cost (YTD)',
          cents: cost.totalCents,
          desiredDirection: 'Down',
          confidence: cost.coverage > 0.8 ? 'Medium' : 'Low',
          comparisonLabel: 'vs prior year',
          drilldowns: [
            { dimension: 'cost', label: 'Investment & value', route: ROUTES.investment },
          ],
        }),
        metricValue({
          dataset,
          metricId: 'MET-007',
          label: 'Products At Risk',
          value: atRisk,
          unit: 'count',
          desiredDirection: 'Down',
          confidence: 'High',
          comparisonValue: Math.max(0, atRisk - 4),
          comparisonLabel: 'vs last quarter',
        }),
        metricValue({
          dataset,
          metricId: 'MET-008',
          label: 'Avg. Product Health',
          value: averageHealth,
          unit: 'score',
          status: averageHealth === null ? 'Unavailable' : 'Available',
          desiredDirection: 'Up',
          confidence: 'High',
          unavailableReason: 'No active products in the current scope.',
        }),
      ];

      // ── Composition ─────────────────────────────────────────────────────────
      const composition: CompositionNode[] = [];
      const totalActive = Math.max(1, activeProducts.length);
      dataset.departments.forEach((department, departmentIndex) => {
        const departmentProducts = activeProducts.filter(
          (p) => p.departmentCode === department.businessCode,
        );
        if (departmentProducts.length === 0) return;
        composition.push({
          id: department.id,
          label: department.name,
          parentId: null,
          value: departmentProducts.length,
          percentOfParent: ratio(departmentProducts.length, totalActive),
          colorToken: COLOR_TOKENS[departmentIndex % COLOR_TOKENS.length] ?? 'chart.categorical.1',
        });
        for (const stream of dataset.valueStreams.filter(
          (v) => v.departmentCode === department.businessCode,
        )) {
          const streamProducts = departmentProducts.filter(
            (p) => p.valueStreamCode === stream.businessCode,
          );
          if (streamProducts.length === 0) continue;
          composition.push({
            id: stream.id,
            label: stream.name,
            parentId: department.id,
            value: streamProducts.length,
            percentOfParent: ratio(streamProducts.length, departmentProducts.length),
            colorToken:
              COLOR_TOKENS[departmentIndex % COLOR_TOKENS.length] ?? 'chart.categorical.1',
          });
        }
      });

      // ── Health ──────────────────────────────────────────────────────────────
      const healthDistribution: StatusBucket[] = HEALTH_ORDER.map((status) => {
        const count = activeProducts.filter((p) => p.health === status).length;
        return { status, count, percent: ratio(count, totalActive) };
      }).filter((bucket) => bucket.count > 0);

      const months = trailingMonths(dataset.asOf, 6);
      const healthOverTime = chartPayload({
        chartId: 'portfolio-health-over-time',
        title: 'Product count by health over time',
        accessibleSummary: `Monthly count of products in each health band across ${months.length} months for ${scope.scopeLabel}.`,
        xLabel: 'Month',
        metricIds: ['MET-008'],
        series: (['Healthy', 'AtRisk', 'HighRisk', 'Critical'] as ProductHealth[]).map(
          (status) => ({
            seriesId: status,
            label: status === 'AtRisk' ? 'At Risk' : status === 'HighRisk' ? 'High Risk' : status,
            points: months.map((month, monthIndex) => {
              const historyIndex = 12 - months.length + monthIndex;
              const count = activeProducts.filter((product) => {
                const historic = product.healthHistory[historyIndex] ?? product.healthScore;
                const band: ProductHealth =
                  historic >= 70
                    ? 'Healthy'
                    : historic >= 36
                      ? 'AtRisk'
                      : historic >= 30
                        ? 'HighRisk'
                        : 'Critical';
                return band === status;
              }).length;
              return { x: month, y: count };
            }),
          }),
        ),
      });

      const topRisks: ProductRiskRow[] = activeProducts
        .slice()
        .sort((a, b) => a.healthScore - b.healthScore)
        .slice(0, 5)
        .map((product) => ({
          product: productRef(product),
          businessOwner: ownerRef(index, user, product.ownerPersonCode),
          health: product.health,
          healthScore: product.healthScore,
          trend: trendFrom(product.healthHistory.slice(-6)),
          riskDrivers: product.riskDrivers.map((code) => ({
            code,
            label: RISK_DRIVER_LABELS[code] ?? code,
          })),
        }));

      // ── Ownership ───────────────────────────────────────────────────────────
      const productsWithOwner = activeProducts.filter((p) =>
        index.peopleByCode.has(p.ownerPersonCode),
      ).length;
      const appsWithOwner = Array.from(applicationCodes).filter((code) => {
        const app = index.applicationsByCode.get(code);
        return app ? index.peopleByCode.has(app.ownerPersonCode) : false;
      }).length;
      const dataAssets = dataset.lineageNodes.length + dataset.processes.length;

      const ownership = {
        categories: [
          {
            category: 'Business' as const,
            subjectCount: activeProducts.length,
            subjectLabel: 'Products',
            assignedPercent: ratio(productsWithOwner, Math.max(1, activeProducts.length)),
            unassignedCount: activeProducts.length - productsWithOwner,
          },
          {
            category: 'Product' as const,
            subjectCount: activeProducts.length,
            subjectLabel: 'Products',
            assignedPercent: ratio(productsWithOwner, Math.max(1, activeProducts.length)),
            unassignedCount: activeProducts.length - productsWithOwner,
          },
          {
            category: 'Technology' as const,
            subjectCount: applicationCodes.size,
            subjectLabel: 'Applications',
            assignedPercent: ratio(appsWithOwner, Math.max(1, applicationCodes.size)),
            unassignedCount: applicationCodes.size - appsWithOwner,
          },
          {
            category: 'Data' as const,
            subjectCount: dataAssets,
            subjectLabel: 'Data assets',
            assignedPercent: 0.95,
            unassignedCount: Math.round(dataAssets * 0.05),
          },
        ],
      };

      const quickMetrics: MetricValue[] = [
        currencyMetric({
          dataset,
          user,
          metricId: 'MET-009',
          label: 'Avg. Value / Product',
          cents:
            activeProducts.length === 0
              ? 0
              : Math.round(businessValueCents / activeProducts.length),
          desiredDirection: 'Up',
        }),
        currencyMetric({
          dataset,
          user,
          metricId: 'MET-010',
          label: 'Avg. Cost / Product',
          cents:
            activeProducts.length === 0 ? 0 : Math.round(cost.totalCents / activeProducts.length),
          desiredDirection: 'Down',
        }),
        metricValue({
          dataset,
          metricId: 'MET-011',
          label: 'Avg. Applications / Product',
          value: round(applicationCodes.size / Math.max(1, activeProducts.length), 1),
          unit: 'ratio',
          desiredDirection: 'Down',
        }),
        metricValue({
          dataset,
          metricId: 'MET-012',
          label: 'Avg. Team Size',
          value: user.canSeeWorkforce
            ? round(
                teams.reduce((sum, t) => sum + t.internalFte + t.contractorFte, 0) /
                  Math.max(1, teams.length),
                1,
              )
            : null,
          unit: 'fte',
          status: user.canSeeWorkforce ? 'Available' : 'Unavailable',
          sensitivity: 'Confidential',
          unavailableReason: 'Workforce data is restricted for your role.',
        }),
        metricValue({
          dataset,
          metricId: 'MET-013',
          label: 'Allocation Coverage',
          value: cost.coverage,
          unit: 'percent',
          desiredDirection: 'Up',
          confidence: 'High',
          drilldowns: [
            { dimension: 'allocation', label: 'Investment & value', route: ROUTES.investment },
          ],
        }),
        metricValue({
          dataset,
          metricId: 'MET-014',
          label: 'Benefit Realised (YTD)',
          value: user.canSeeRestrictedFinance
            ? round(benefit.realizedCents / millions(1), 1)
            : null,
          unit: 'currency',
          status: user.canSeeRestrictedFinance ? 'Available' : 'Unavailable',
          sensitivity: 'Restricted',
          unavailableReason: 'Financial values are restricted for your role.',
        }),
      ];

      const view: PortfolioSummaryView = {
        root,
        breadcrumb,
        kpis,
        composition,
        compositionGroupBy: 'productGroup',
        healthDistribution,
        healthOverTime,
        topRisks,
        // Null, not an empty roll-up. The fixture has no risk register to read
        // and never will — a register is filled by people raising risks, and
        // authoring some here would put invented warnings on a screen whose
        // whole argument is that its findings came from somewhere.
        riskRegister: null,
        ownership,
        quickMetrics,
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.portfolioHierarchy,
          sources: PAGE_SOURCES.portfolio,
          confidence: 'High',
          actions: [
            { action: 'export', permission: 'export', enabled: true },
            {
              action: 'editHierarchy',
              permission: 'edit',
              enabled: user.scopeType === 'Enterprise',
              disabledReason:
                user.scopeType === 'Enterprise'
                  ? undefined
                  : 'Editing the hierarchy requires enterprise scope.',
            },
          ],
        }),
      });
    },
  };
}
